import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MockInterviewQuestionsSubTab.css';

const API_URL = process.env.REACT_APP_API_URL || '';

function MockInterviewQuestionsSubTab() {
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [interviews, setInterviews] = useState([]);
  const [formData, setFormData] = useState({
    mock_interview_id: '',
    question_text: '',
    correct_answer: ''
  });
  const [regeneratingAnswer, setRegeneratingAnswer] = useState(false);

  useEffect(() => {
    fetchQuestions();
    fetchInterviews();
  }, []);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/mock-interview-questions`);
      setQuestions(response.data);
      setError(null);
    } catch (err) {
      setError('Ошибка при загрузке вопросов: ' + (err.response?.data?.detail || err.message));
    } finally {
      setLoading(false);
    }
  };

  const fetchInterviews = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/mock-interviews`);
      setInterviews(response.data);
    } catch (err) {
      console.error('Ошибка при загрузке mock-собеседований:', err);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const submitData = {
        ...formData,
        mock_interview_id: parseInt(formData.mock_interview_id)
      };
      if (editingQuestion) {
        await axios.put(`${API_URL}/api/mock-interview-questions/${editingQuestion.id}`, submitData);
      } else {
        await axios.post(`${API_URL}/api/mock-interview-questions`, submitData);
      }
      setShowForm(false);
      setEditingQuestion(null);
      setRegeneratingAnswer(false);
      setFormData({
        mock_interview_id: '',
        question_text: '',
        correct_answer: ''
      });
      fetchQuestions();
    } catch (err) {
      setError('Ошибка при сохранении вопроса: ' + (err.response?.data?.detail || err.message));
    }
  };

  // Проксирует текст вопроса через админ-бэкенд на бэкенд нейросети (generate-correct-answer), подставляет ответ в поле «Правильный ответ»
  const handleRegenerateAnswer = async () => {
    const questionText = (formData.question_text || '').trim();
    if (!questionText) {
      setError('Введите текст вопроса для пересоздания ответа');
      return;
    }
    try {
      setRegeneratingAnswer(true);
      setError(null);
      const response = await axios.post(`${API_URL}/api/questions/regenerate-answer`, {
        question_text: questionText,
      });
      const answer = response.data?.correct_answer ?? '';
      setFormData((prev) => ({ ...prev, correct_answer: answer }));
    } catch (err) {
      setError('Ошибка при пересоздании ответа: ' + (err.response?.data?.detail || err.message));
    } finally {
      setRegeneratingAnswer(false);
    }
  };

  const handleEdit = (question) => {
    setEditingQuestion(question);
    setFormData({
      mock_interview_id: question.mock_interview_id.toString(),
      question_text: question.question_text,
      correct_answer: question.correct_answer
    });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Вы уверены, что хотите удалить этот вопрос?')) {
      try {
        await axios.delete(`${API_URL}/api/mock-interview-questions/${id}`);
        fetchQuestions();
      } catch (err) {
        setError('Ошибка при удалении вопроса: ' + (err.response?.data?.detail || err.message));
      }
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingQuestion(null);
    setRegeneratingAnswer(false);
    setFormData({
      mock_interview_id: '',
      question_text: '',
      correct_answer: ''
    });
  };

  if (loading) {
    return <div className="loading">Загрузка...</div>;
  }

  return (
    <div className="mock-interview-questions-sub-tab">
      <div className="tab-header">
        <h3>Управление вопросами для Mock-собеседований</h3>
        {!showForm && (
          <button className="btn btn-primary" onClick={() => setShowForm(true)}>
            + Добавить вопрос
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {showForm && (
        <div className="form-container">
          <h4>{editingQuestion ? 'Редактировать вопрос' : 'Добавить новый вопрос'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="mock_interview_id">Mock-собеседование *</label>
              <select
                id="mock_interview_id"
                value={formData.mock_interview_id}
                onChange={(e) => setFormData({ ...formData, mock_interview_id: e.target.value })}
                required
              >
                <option value="">Выберите mock-собеседование</option>
                {interviews.map((interview) => (
                  <option key={interview.id} value={interview.id}>
                    {interview.title} ({interview.company_name || 'Без компании'})
                  </option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="question_text">Текст вопроса *</label>
              <textarea
                id="question_text"
                value={formData.question_text}
                onChange={(e) => setFormData({ ...formData, question_text: e.target.value })}
                required
                rows={4}
                placeholder="Введите текст вопроса"
              />
            </div>
            <div className="form-group">
              <div className="form-group-label-row">
                <label htmlFor="correct_answer">Правильный ответ *</label>
                <button
                  type="button"
                  className="btn btn-secondary btn-regenerate-answer"
                  onClick={handleRegenerateAnswer}
                  disabled={regeneratingAnswer || !(formData.question_text || '').trim()}
                  title={!(formData.question_text || '').trim() ? 'Сначала введите текст вопроса' : 'Пересоздать эталонный ответ нейросетью'}
                >
                  {regeneratingAnswer ? '⏳ Пересоздание...' : '🔄 Пересоздать ответ'}
                </button>
              </div>
              <textarea
                id="correct_answer"
                value={formData.correct_answer}
                onChange={(e) => setFormData({ ...formData, correct_answer: e.target.value })}
                required
                rows={6}
                placeholder="Введите правильный ответ на вопрос"
                disabled={regeneratingAnswer}
              />
              <small className="form-hint">
                Кнопка «Пересоздать ответ» отправит текст вопроса в нейросеть и подставит эталонный ответ в поле выше. Можно нажимать многократно.
              </small>
            </div>
            <div className="form-actions">
              <button type="submit" className="btn btn-primary">
                {editingQuestion ? 'Сохранить' : 'Создать'}
              </button>
              <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                Отмена
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Mock-собеседование</th>
              <th>Вопрос</th>
              <th>Правильный ответ</th>
              <th>Действия</th>
            </tr>
          </thead>
          <tbody>
            {questions.length === 0 ? (
              <tr>
                <td colSpan="5" className="empty-state">
                  Нет вопросов. Добавьте первый вопрос.
                </td>
              </tr>
            ) : (
              questions.map((question) => (
                <tr key={question.id}>
                  <td>{question.id}</td>
                  <td>{question.mock_interview_title || '-'}</td>
                  <td className="question-text">{question.question_text}</td>
                  <td className="answer-text">{question.correct_answer}</td>
                  <td>
                    <button
                      className="btn-icon btn-edit"
                      onClick={() => handleEdit(question)}
                      title="Редактировать"
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon btn-delete"
                      onClick={() => handleDelete(question.id)}
                      title="Удалить"
                    >
                      ✕
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default MockInterviewQuestionsSubTab;

